import { domQuerySelector } from 'pf-frontend-common/src/helper/dom/query-selector';
import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';
import { EventEmitterAwareInterface } from 'pf-frontend-common/src/module/event/emitter-aware.interface';
import { ViewBasicInterface } from 'pf-frontend-common/src/module/view/basic.interface';

import { i18nTranslate } from 'common/helper/i18n/translate';
import { InputSelectOptionInterface } from 'common/module/input/select/option.interface';
import { MyAccountFilterOptionsInterface } from 'common/module/my-account/filter-options.interface';
import { MyAccountFormOptionsInterface } from 'common/module/my-account/form-options.interface';
import { MyAccountListTypeEnum } from 'common/module/my-account/list-type.enum';
import { MyAccountStatechartEvent } from 'common/module/my-account/statechart.event';
import { MyAccountViewCssSelectorEnum } from 'common/module/my-account/view-css-selector.enum';
import { MyAccountViewOptionsInterface } from 'common/module/my-account/view-options.interface';
import { StatechartStoreInterface } from 'common/module/statechart/store.interface';
import { ViewDynamicInterface } from 'common/module/view/dynamic.interface';
import { MyAccountHeaderViewStore } from 'desktop/module/my-account/header/view-store';
import { MyAccountHeaderViewStoreEvent } from 'desktop/module/my-account/header/view-store.event';
import { MyAccountSelectBoxInterface } from 'desktop/module/my-account/select-box.interface';

export class MyAccountViewStrategy implements ViewBasicInterface, EventEmitterAwareInterface {
  /**
   * Main HTML element
   */
  private element: HTMLElement;

  /**
   * Filter options list
   */
  private filterOptions: MyAccountFilterOptionsInterface;

  /**
   * Constructor
   */
  constructor(
    private myAccountHeaderViewDynamic: ViewDynamicInterface<MyAccountHeaderViewStore>,
    private eventEmitter: EventEmitterInterface,
    private myAccountStatechartStore: StatechartStoreInterface
  ) {}

  /**
   * @inheritDoc
   */
  public initialize(options: MyAccountViewOptionsInterface): void {
    // Keep HTML element
    this.element = options.element;

    // Initialize subviews
    this.initMyAccountHeader(options.listType, options.categories, options.sort);
  }

  /**
   * @inheritDoc
   */
  public render(): void {
    this.renderMyAccountHeader();
  }

  /**
   * @inheritDoc
   */
  public observers(): void {
    return;
  }

  /**
   * @inheritDoc
   */
  public getEventEmitter(): EventEmitterInterface {
    return this.eventEmitter;
  }

  /**
   * Init my account header
   */
  private initMyAccountHeader(
    listType: MyAccountListTypeEnum,
    categories?: MyAccountFormOptionsInterface,
    filters?: MyAccountFormOptionsInterface
  ): void {
    this.myAccountHeaderViewDynamic.initialize({
      element: domQuerySelector(this.element, MyAccountViewCssSelectorEnum.header),
    });

    this.myAccountHeaderViewDynamic.getViewStore().initialize({
      categories: {
        choices: this.selectOptionsTransformer(categories?.choices),
        value: categories?.value,
      },
      filters: {
        choices: this.selectOptionsTransformer(filters?.choices),
        value: filters?.value,
      },
      listType,
      showDeleteAll: listType === MyAccountListTypeEnum.searches,
    });

    this.myAccountHeaderViewDynamic
      .getViewStore()
      .getEventEmitter()
      .addListener(MyAccountHeaderViewStoreEvent.filterSavedProperties, this.onFilterSavedPropertiesMyAccountHeader);

    this.myAccountHeaderViewDynamic
      .getViewStore()
      .getEventEmitter()
      .addListener(MyAccountHeaderViewStoreEvent.sortSavedProperties, this.onSortSavedPropertiesMyAccountHeader);

    this.myAccountHeaderViewDynamic
      .getViewStore()
      .getEventEmitter()
      .addListener(
        MyAccountHeaderViewStoreEvent.deleteAllSavedProperties,
        this.onDeleteAllSavedPropertiesMyAccountHeader
      );
  }

  /**
   * Render my account header
   */
  private renderMyAccountHeader(): void {
    this.myAccountHeaderViewDynamic.render();
  }

  /**
   * Select options transformer
   */
  private selectOptionsTransformer(collection: MyAccountSelectBoxInterface[] = []): InputSelectOptionInterface[] {
    return collection.map((category: MyAccountSelectBoxInterface) => ({
      value: category.value + '',
      request_value: category.request_value,
      text: i18nTranslate(category.label),
    }));
  }

  /**
   * Delete all saved properties
   */
  private onDeleteAllSavedPropertiesMyAccountHeader = (): void => {
    this.myAccountStatechartStore.transit({
      event: MyAccountStatechartEvent.deleteAll,
      payload: {},
    });
  };

  /**
   * Filter saved properties
   */
  private onFilterSavedPropertiesMyAccountHeader = (categoryId: string, sort: string): void => {
    this.filterOptions = {
      categoryId,
      sort,
    };

    this.myAccountStatechartStore.transit({
      event: MyAccountStatechartEvent.filter,
      payload: this.filterOptions,
    });
  };

  /**
   * Sort saved properties
   */
  private onSortSavedPropertiesMyAccountHeader = (option: string): void => {
    this.filterOptions = {
      ...this.filterOptions,
      sort: option,
    };

    this.myAccountStatechartStore.transit({
      event: MyAccountStatechartEvent.sort,
      payload: this.filterOptions,
    });
  };
}
